import { useCallback, useEffect, useRef, useState } from 'react';
import { useLayerStore, useMapStore } from '@uofu-uss-gis/ui-react';
import { EVENT_PARKING_LAYER_CONFIG, buildEventParkingServiceUrl } from '../../config/eventParkingConfig';
import { fetchLayerDynamicLegend, type DynamicLegendItem } from '../../utils/eventParkingLegendUtils';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';

interface EventParkingPanelProps {
  onClose?: () => void;
}

function EventParkingPanel({ onClose }: EventParkingPanelProps) {
  const layers = useLayerStore((state) => state.layers);
  const mapView = useMapStore((state) => state.mapView);
  const theme = useTheme();
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupOffsetY, setPopupOffsetY] = useState(0);
  const [dynamicLegend, setDynamicLegend] = useState<DynamicLegendItem[]>([]);
  const [legendLoading, setLegendLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Find which event parking layer is active (if any) — must be declared before any hook that references it
  const activeEvent = EVENT_PARKING_LAYER_CONFIG.find(
    (eventLayer) => layers[eventLayer.layerId]
  );

  const getPopupContainer = useCallback((): HTMLElement | null => {
    const popup = mapView?.popup as { container?: HTMLElement | null } | undefined;
    return popup?.container ?? document.querySelector<HTMLElement>('.esri-popup');
  }, [mapView]);

  // Fetch legend items from the renderer of each layer associated with the active event
  useEffect(() => {
    if (!activeEvent) {
      setDynamicLegend([]);
      return;
    }

    setLegendLoading(true);
    const serviceUrl = buildEventParkingServiceUrl();
    const indices = Array.isArray(activeEvent.serviceLayerIndex)
      ? activeEvent.serviceLayerIndex
      : [activeEvent.serviceLayerIndex];

    Promise.all(indices.map((idx) => fetchLayerDynamicLegend(`${serviceUrl}/${idx}`)))
      .then((results) => {
        // Flatten all layers' items, deduplicating by label across layers
        const seen = new Set<string>();
        const combined = results.flat().filter((item) => {
          if (!item.label || seen.has(item.label)) return false;
          seen.add(item.label);
          return true;
        });
        setDynamicLegend(combined);
        setLegendLoading(false);
      })
      .catch((err) => {
        console.error('[EventParkingPanel] Failed to fetch dynamic legend:', err);
        setLegendLoading(false);
      });
  }, [activeEvent]);

  useEffect(() => {
    if (!activeEvent) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (window.innerWidth >= 768) {
        return;
      }

      const target = event.target as Node;
      if (panelRef.current?.contains(target)) {
        return;
      }

      setIsCollapsed(true);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [activeEvent, isCollapsed]);

  useEffect(() => {
    if (!mapView?.popup) {
      setIsPopupOpen(false);
      return;
    }

    setIsPopupOpen(Boolean(mapView.popup.visible));

    const popupVisibleWatcher = mapView.popup.watch('visible', (visible: boolean) => {
      setIsPopupOpen(Boolean(visible));
    });

    return () => {
      popupVisibleWatcher?.remove();
    };
  }, [mapView]);

  useEffect(() => {
    if (activeEvent) {
      setIsCollapsed(false);
      setIsMoreInfoOpen(false);
    }
  }, [activeEvent]);

  useEffect(() => {
    if (isPopupOpen) {
      setIsMoreInfoOpen(false);
    }
  }, [isPopupOpen]);

  useEffect(() => {
    if (!isPopupOpen) {
      setPopupOffsetY(0);
      return;
    }

    const computePopupOffset = () => {
      const popupElement = getPopupContainer();
      if (!popupElement) {
        setPopupOffsetY(window.innerWidth >= 768 ? 180 : 140);
        return;
      }

      const popupRect = popupElement.getBoundingClientRect();
      const baseTop = window.innerWidth >= 768 ? 16 : 80;
      const popupGap = 12;
      const viewportPadding = 12;
      const estimatedPanelHeight = panelRef.current?.offsetHeight ?? (window.innerWidth >= 768 ? 220 : 200);
      const maxTop = Math.max(baseTop, window.innerHeight - estimatedPanelHeight - viewportPadding);
      const desiredTop = popupRect.bottom + popupGap;
      const clampedTop = Math.min(Math.max(baseTop, desiredTop), maxTop);

      setPopupOffsetY(Math.max(0, clampedTop - baseTop));
    };

    computePopupOffset();
    window.addEventListener('resize', computePopupOffset);

    const popupElement = getPopupContainer();
    let popupResizeObserver: ResizeObserver | null = null;
    if (popupElement && 'ResizeObserver' in window) {
      popupResizeObserver = new ResizeObserver(computePopupOffset);
      popupResizeObserver.observe(popupElement);
    }

    return () => {
      window.removeEventListener('resize', computePopupOffset);
      popupResizeObserver?.disconnect();
    };
  }, [getPopupContainer, isPopupOpen]);

  // If no event is active, don't render
  if (!activeEvent) {
    return null;
  }

  const handleCollapse = () => {
    setIsCollapsed(true);
    onClose?.();
  };

  const popupShiftStyle = isPopupOpen && popupOffsetY > 0
    ? { marginTop: `${popupOffsetY}px` }
    : undefined;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsCollapsed(false)}
        style={{ ...popupShiftStyle, backgroundColor: theme.palette.background.paper, borderColor: theme.palette.divider }}
        className={`absolute top-24 md:top-8 right-0 z-50 rounded-l-md md:rounded-l-lg md:bg-[#BE0000] border md:border-[#8f0000] shadow-md md:shadow-xl px-2 py-3 md:px-3 md:py-3 flex items-center gap-1 md:gap-2 transition-all duration-300 ease-out ${isCollapsed ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}`}
        aria-label="Reopen event parking panel"
      >
        <ChevronLeftIcon fontSize="small" className="text-[#BE0000] md:text-white" />
        <span className="text-xs md:text-sm font-semibold md:text-white hidden sm:inline" style={{ color: theme.palette.text.primary }}>Event Parking</span>
      </button>

      <div
        ref={panelRef}
        style={{ ...popupShiftStyle, backgroundColor: theme.palette.background.paper, borderColor: theme.palette.divider, color: theme.palette.text.primary }}
        className={`absolute top-20 md:top-4 right-4 rounded-lg shadow-lg border z-50 overflow-y-auto transition-all duration-300 ease-out ${isPopupOpen ? 'w-56 md:w-60 max-h-[32dvh] md:max-h-[36dvh]' : 'w-64 md:w-72 max-h-[62dvh] md:max-h-[calc(100vh-2rem)]'} ${isCollapsed ? 'translate-x-[120%] pointer-events-none' : 'translate-x-0 opacity-100'}`}
      >
        {/* Header */}
        <div
          style={{ borderColor: theme.palette.divider, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#F9FAFB' }}
          className={`flex items-center border-b rounded-t-lg ${isPopupOpen ? 'justify-end p-1' : 'justify-between p-3'}`}
        >
          {!isPopupOpen && <p className="font-bold text-sm">{activeEvent.details.activeTitle}</p>}
          <IconButton size="small" onClick={handleCollapse} aria-label="Collapse event parking panel">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Content */}
        <div className={isPopupOpen ? 'p-2' : 'p-3'}>
          {/* Dynamic Legend */}
          <div className="mb-3">
            <p className="font-semibold text-xs uppercase mb-2" style={{ color: theme.palette.text.secondary }}>Parking Types</p>
            <div className={`${isPopupOpen ? 'max-h-36 md:max-h-40' : 'max-h-56 md:max-h-none'} overflow-y-auto pr-1 md:overflow-visible`}>
              {legendLoading ? (
                <p className="text-xs" style={{ color: theme.palette.text.secondary }}>Loading legend…</p>
              ) : (
                dynamicLegend.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 mb-1.5">
                    {item.imageDataUrl ? (
                      <img
                        src={item.imageDataUrl}
                        alt={item.label}
                        style={{ width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', objectFit: 'contain' }}
                        className="flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="flex-shrink-0 rounded"
                        style={{
                          backgroundColor: item.fillColor ?? 'transparent',
                          border: item.outlineColor
                            ? `2px solid ${item.outlineColor}`
                            : '1px solid #D1D5DB',
                          boxSizing: 'border-box',
                          width: '16px',
                          height: '16px',
                          minWidth: '16px',
                          minHeight: '16px',
                        }}
                      />
                    )}
                    <span className="text-sm" style={{ color: theme.palette.text.primary }}>{item.label}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* More Info Toggle */}
          <div className={isPopupOpen ? 'hidden' : 'border-t pt-2'} style={{ borderColor: theme.palette.divider }}>
            <button
              type="button"
              onClick={() => setIsMoreInfoOpen((prev) => !prev)}
              className="w-full flex items-center justify-between text-sm font-semibold"
              style={{ color: theme.palette.text.primary }}
              aria-expanded={isMoreInfoOpen}
            >
              <span>More Info</span>
              <ExpandMoreIcon fontSize="small" className={`transition-transform ${isMoreInfoOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className={!isPopupOpen && isMoreInfoOpen ? 'block' : 'hidden'}>
            {/* Notes */}
            {activeEvent.details.notes.length > 0 && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.palette.divider }}>
                <p className="font-semibold text-xs uppercase mb-2" style={{ color: theme.palette.text.secondary }}>Important Information</p>
                {activeEvent.details.notes.map((note, index) => {
                  const colonIndex = note.indexOf(':');
                  const hasLabel = colonIndex > 0;
                  const label = hasLabel ? note.substring(0, colonIndex + 1) : '';
                  const content = hasLabel ? note.substring(colonIndex + 1) : note;
                  return (
                    <p key={index} className="text-sm mb-2 last:mb-0 leading-relaxed" style={{ color: theme.palette.text.primary }}>
                      {hasLabel && <strong style={{ color: theme.palette.info.main }}>{label}</strong>}
                      {content}
                    </p>
                  );
                })}
              </div>
            )}

            {/* Downloadable Map Link */}
            {activeEvent.downloadableMapUrl && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.palette.divider }}>
                <a
                  href={activeEvent.downloadableMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-md underline hover:no-underline"
                  style={{ color: theme.palette.info.main }}
                >
                  Downloadable Map
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EventParkingPanel;
